import {put, call, all} from 'redux-saga/effects';
import Privilege from "../../entities/privilege";
import * as Actions from "../actions/privilegeActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type PrivilegeAction = {
    privilegeId: string;
    privilege: Privilege;
    privileges: Privilege[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOnePrivilege(action: PrivilegeAction) {
	const session = tabSession.captureGeneration();
	try {
		const privilege: Privilege = yield call(api.findOne, 'privilege', action.privilegeId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.PRIVILEGE_FETCH_ONE_SUCCESS, privilegeId: action.privilegeId, privilege, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.PRIVILEGE_FETCH_ONE_FAILURE, privilegeId: action.privilegeId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {PrivilegeAction} action
 */
export function* savePrivilege(action: PrivilegeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ PRIVILEGE_SAVE saga', action.privilege?.id);

		if (action.privilege === undefined || action.privilege === null) {
			console.error('savePrivilege received a null');
			throw new Error('savePrivilege received a null');
		}

		if (!(action.privilege instanceof Privilege)) {
			action.privilege = new Privilege(action.privilege);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.privilege.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.privilege);
			const addResult = yield call(idb.add, 'privilege', action.privilege);
			if (addResult.isErr()) {
				console.error(`⮂ savePrivilege: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertPrivilegeSave(action.privilege, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.PRIVILEGE_SAVE_FAILURE, privilege: action.privilege, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.PRIVILEGE_SAVE, privilege: action.privilege, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertPrivilegeSave(action.privilege, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.PRIVILEGE_SAVE_FAILURE, privilege: action.privilege, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {PrivilegeAction} action
 * @param {Privilege[]} action.privileges
 */
export function* saveManyPrivileges(action: PrivilegeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ PRIVILEGE_SAVE_MANY saga', action.privileges.map(privilege => privilege.id).join(','));

        if (!Array.isArray(action.privileges)) {
            console.error('saveManyPrivileges expected an array, received', action.privileges);
            throw new Error('saveManyPrivileges expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see savePrivilege), under these queue keys
		const dispatchedAddedOns: number[] = action.privileges.map(privilege => privilege?.addedOn);
		const revertAll = () => action.privileges.map((privilege, i) =>
			put(Actions.revertPrivilegeSave(privilege, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Privilege[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.privileges.length; i++) {
				const privilege = action.privileges[i];
				privilege.persisted = false;
				ensureOperationId(privilege);
				toSave.push(privilege);
			}

			const addResult = yield call(idb.add, 'privilege', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyPrivileges: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.PRIVILEGE_SAVE_MANY_FAILURE, privileges: action.privileges, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.PRIVILEGE_SAVE_MANY, privileges: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.PRIVILEGE_SAVE_MANY_FAILURE, privileges: action.privileges, status: 500, err});

			console.error(`Error saving ${action.privileges.length} privileges to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {PrivilegeAction} action
 */
export function* removePrivilege(action: PrivilegeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.privilege === undefined || action.privilege === null || !(action.privilege instanceof Privilege)) {
			console.error('removePrivilege expected type Privilege, received', action.privilege);
			reportQueued(false);
			throw new Error('removePrivilege expected type Privilege');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.privilege.status = 'D';
		action.privilege.addedOn = newHRTimestamp();
		action.privilege.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.privilege);
		const addResult = yield call(idb.add, 'privilege', action.privilege);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removePrivilege: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.PRIVILEGE_REMOVE_FAILURE, privilege: action.privilege, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.PRIVILEGE_REMOVE, privilege: action.privilege});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.PRIVILEGE_REMOVE_FAILURE, privilege: action.privilege, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Privilege.
	*
	* @param {PrivilegeAction} action
	*/
export function* restorePrivilege(action: PrivilegeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restorePrivilege expected getCurrentUserId() to return a value');
        return;
    }

	if (action.privilege === undefined || action.privilege === null) {
        console.error('restorePrivilege expected a Privilege, received null');
        throw new Error('restorePrivilege expected a Privilege, received null');
    }
    else if (!(action.privilege instanceof Privilege)) action.privilege = new Privilege(action.privilege); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.privilege.status = 'U';
		action.privilege.addedOn = newHRTimestamp();
		action.privilege.addedBy = userId;
		action.privilege.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.privilege);
		const addResult = yield call(idb.add, 'privilege', action.privilege);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restorePrivilege: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.PRIVILEGE_RESTORE_FAILURE, privilege: action.privilege, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.PRIVILEGE_RESTORE, privilege: action.privilege});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.PRIVILEGE_RESTORE_FAILURE, privilege: action.privilege, status: 500, err});

		console.error('Error restoring from database', err);
	}
}